import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { ToastService } from 'app/services/toast.service';
import { Observable, retry, throwError, timer, timeout, catchError, EMPTY } from 'rxjs';
import { TimeoutError } from 'rxjs';

// ─────────────────────────────────────────────────────────────────────────────
// Global HTTP Error Interceptor
//
// Runs AFTER the auth interceptor in the chain. Handles:
//  1. Request timeout (30s)
//  2. Automatic retry for 5xx and network errors (1 retry, 2s delay)
//  3. User-friendly toast notifications for non-401 errors
//     (401 is handled by auth interceptor → sign-out + reload)
// ─────────────────────────────────────────────────────────────────────────────

const REQUEST_TIMEOUT_MS = 30_000; // 30 seconds
const RETRY_COUNT = 1;
const RETRY_DELAY_MS = 2_000; // 2 seconds

/**
 * Endpoints that legitimately run far longer than REQUEST_TIMEOUT_MS, and so
 * get a much larger budget of their own.
 *
 * The region import rebuilds the whole address master in one transaction —
 * ~165k areas, 6.6k cities and 754 districts, soft-deleted and re-inserted —
 * which takes well over a minute. At 30s the browser gave up while the server
 * was still committing, so the UI reported "Import failed. Nothing was changed."
 * for an import that had in fact succeeded.
 *
 * Deliberately a URL allowlist rather than a bigger global timeout: 30s is the
 * right default for ordinary calls, and raising it everywhere would make every
 * genuinely hung request take minutes to surface.
 */
const LONG_RUNNING_URL_PATTERNS = ['/saasRegionImport/'];
const LONG_RUNNING_TIMEOUT_MS = 10 * 60_000; // 10 minutes

function timeoutForRequest(url: string): number {
    return LONG_RUNNING_URL_PATTERNS.some((p) => url.includes(p))
        ? LONG_RUNNING_TIMEOUT_MS
        : REQUEST_TIMEOUT_MS;
}

/** Returns true for errors that are worth retrying (server errors, network failures) */
function isRetryable(error: HttpErrorResponse): boolean {
    // Network error (status 0 = no response received)
    if (error.status === 0) return true;

    // Server errors (5xx)
    if (error.status >= 500 && error.status < 600) return true;

    // 408 Request Timeout, 429 Too Many Requests
    if (error.status === 408 || error.status === 429) return true;

    return false;
}

/** Maps HTTP errors to user-friendly messages */
function getUserMessage(error: HttpErrorResponse | TimeoutError): string | null {
    // Timeout from RxJS timeout operator
    if (error instanceof TimeoutError) {
        return 'Request timed out — the server took too long to respond.';
    }

    // 401 — handled by auth interceptor, don't show toast
    if (error.status === 401) return null;

    // Network error
    if (error.status === 0) {
        return 'Network error — please check your internet connection.';
    }

    // Client errors (4xx)
    if (error.status === 403) return 'Access denied — you don\'t have permission for this action.';
    if (error.status === 404) return null; // Let component handle 404 (may be expected)
    if (error.status === 429) return 'Too many requests — please wait a moment and try again.';

    // Server errors (5xx) — after retry exhaustion
    if (error.status >= 500) {
        return 'Server error — please try again in a moment.';
    }

    // Other errors — let component handle
    return null;
}

function isUnauthorizedLike(error: HttpErrorResponse | TimeoutError): boolean {
    if (error instanceof TimeoutError) {
        return false;
    }

    if (error.status === 401) {
        return true;
    }

    const backendStatus = Number((error.error as any)?.status);
    if (backendStatus === 401) {
        return true;
    }

    const message = String(
        (error.error as any)?.message ?? error.message ?? ''
    ).toLowerCase();
    return (
        message.includes('unauthorized request') ||
        message.includes('session expired') ||
        message.includes('invalid session')
    );
}

export const errorInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const toast = inject(ToastService);
    const authService = inject(AuthService);

    return next(req).pipe(
        // Timeout: fail if the server doesn't respond in time. 30s for ordinary
        // calls; bulk endpoints get their own budget (see LONG_RUNNING_URL_PATTERNS).
        timeout(timeoutForRequest(req.urlWithParams)),

        // Retry: only for retryable errors (5xx, network), 1 retry with 2s delay
        retry({
            count: RETRY_COUNT,
            delay: (error: HttpErrorResponse | TimeoutError, retryCount: number) => {
                // TimeoutError is not retryable
                if (error instanceof TimeoutError) {
                    return throwError(() => error);
                }

                if (isRetryable(error)) {
                    console.warn(
                        `[ErrorInterceptor] Retry ${retryCount}/${RETRY_COUNT} for ${req.method} ${req.urlWithParams} (${error.status})`
                    );
                    return timer(RETRY_DELAY_MS);
                }
                // Non-retryable → don't retry, throw immediately
                return throwError(() => error);
            },
        }),

        // After retries exhausted, show toast and re-throw
        catchError((error: HttpErrorResponse | TimeoutError) => {
            const isSignInRequest = req.url.includes('systemUserLogin');
            // signOut()'s own teardown calls — a 401 there must not re-enter
            // signOut() and reload the page mid sign-out.
            const isSignOutRequest =
                req.url.includes('/userLogout') ||
                req.url.includes('/admin-push/unsubscribe');
            if (!isSignInRequest && !isSignOutRequest && isUnauthorizedLike(error)) {
                authService.signOut();
                location.reload();
                return EMPTY;
            }

            const message = getUserMessage(error);
            if (message) {
                toast.error(message);
            }

            // Log for debugging
            if (error instanceof TimeoutError) {
                console.error(
                    `[ErrorInterceptor] Timeout for ${req.method} ${req.urlWithParams}`
                );
            } else if (error.status !== 401) {
                console.error(
                    `[ErrorInterceptor] ${req.method} ${req.urlWithParams} → ${error.status}`,
                    error.message
                );
            }

            return throwError(() => error);
        })
    );
};
